import { useDispatch } from "react-redux";

import { CloseIcon } from "@/assets/icons";
import { setSidebarUserSettingPopupDisplay } from "@/store/slice/uiSlice";
import { GetUserByIdDetail, UserStatus } from "@/types/user";

import { Button } from "equator-design-system";

interface ViewUserPanelProps {
  userData: GetUserByIdDetail;
  onResendActivationClick?: () => void;
  onDelete: () => void;
}

const StatusMapping = {
  [UserStatus.ACTIVE]: "Active",
  [UserStatus.INACTIVE]: "Inactive",
  [UserStatus.NOT_VERIFIED]: "Not Verified"
};

const ViewUserPanel = ({
  userData,
  onResendActivationClick,
  onDelete
}: ViewUserPanelProps) => {
  const dispatch = useDispatch();

  const userFieldNull = "-";

  const handleClose = () => {
    dispatch(
      setSidebarUserSettingPopupDisplay({
        isPanelView: false,
        isPanelAdd: false,
        isPanelEdit: false
      })
    );
  };

  const handleEdit = () => {
    dispatch(
      setSidebarUserSettingPopupDisplay({
        isPanelView: false,
        isPanelAdd: false,
        isPanelEdit: true
      })
    );
  };

  return (
    <div
      className="
        absolute top-4 right-4 z-[9] border-gainsboro bg-white rounded-lg w-[456px] h-[calc(100vh-32px)] overflow-y-auto
        flex flex-col"
    >
      <div className="sticky top-0 bg-white z-10 px-[24px] py-3">
        <div className="flex flex-row justify-between items-center">
          <h2 className="text-txtHeading font-sans font-bold text-[20px]">
            User Information
          </h2>
          <div className="flex gap-3">
            <button
              aria-label="close"
              onClick={handleClose}
              className="bg-transparent border-0 p-0"
              data-testid="btn-header-close"
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-5 mt-3">
        <div className="flex flex-col px-[24px]">
          <span className="font-sans font-semibold pb-[12px] text-txtHeading text-sm">
            Personal Information
          </span>

          <div className="space-y-3 mt-3">
            <div className="flex items-center justify-between text-sm">
              <div className="">Full Name</div>
              <div className="font-semibold  text-txtHeading">
                {userData?.full_name ?? userFieldNull}
              </div>
            </div>
            <div className="flex items-start justify-between text-sm">
              <div className="">Email</div>
              <div className="flex flex-col gap-1 items-end">
                <div className="font-semibold  text-txtHeading">
                  {userData?.email_address ?? userFieldNull}
                </div>
                {userData?.status === UserStatus.NOT_VERIFIED && (
                  <button
                    type="button"
                    className="font-semibold  text-primary-teal underline"
                    onClick={onResendActivationClick}
                  >
                    Resend verification link
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="">Phone Number</div>
              <div className="font-semibold flex gap-1 text-txtHeading">
                <span>{userData?.phone_number?.trim()}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col px-[24px]">
          <span className="font-sans font-semibold pb-[12px] text-txtHeading text-sm">
            Country and Languages
          </span>
          <div className="space-y-3 mt-3">
            <div className="flex items-center justify-between text-sm">
              <div className="">Country</div>
              <div className="font-semibold  text-txtHeading">
                {userData?.country.name ?? userFieldNull}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="">Timezone</div>
              <div className="font-semibold  text-txtHeading">
                {userData?.timezone.name ?? userFieldNull}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="">Language</div>
              <div className="font-semibold  text-txtHeading">
                {userData?.language.name ?? userFieldNull}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col px-[24px]">
          <span className="font-sans font-semibold pb-[12px] text-txtHeading text-sm">
            Status
          </span>
          <div className="space-y-3 mt-3">
            <div className="flex items-center justify-between text-sm">
              <div className="">Status</div>
              <div className="font-semibold  text-txtHeading">
                {userData?.status
                  ? StatusMapping[userData?.status]
                  : userFieldNull}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex px-4 justify-center gap-2 absolute bottom-0 bg-white w-full py-[20px] rounded-t-none rounded-lg">
        <Button
          variant="secondary"
          className="h-[40px] w-[198px] btn-secondary text-sm !font-bold"
          onClick={onDelete}
          type="button"
        >
          <span>Delete User</span>
        </Button>
        <Button
          onClick={handleEdit}
          className="h-[40px] w-[198px] btn-primary text-sm !font-bold"
          type="button"
        >
          <span>Edit User</span>
        </Button>
      </div>
    </div>
  );
};

export default ViewUserPanel;
